/**
 * Creates a throttled version of a function that will only execute once per specified time period.
 * When called multiple times within the throttle period, only the first call will execute immediately,
 * and subsequent calls within the period will be ignored.
 *
 * @param func - The function to throttle
 * @param wait - The time period in milliseconds to throttle calls
 * @returns A throttled version of the function
 *
 * @example
 * ```typescript
 * const throttledSave = throttleFn(saveData, 1000);
 *
 * // First call executes immediately
 * throttledSave();
 *
 * // These calls within 1 second are ignored
 * throttledSave();
 * throttledSave();
 *
 * // After 1 second, the next call will execute
 * setTimeout(() => throttledSave(), 1100);
 * ```
 */
export function throttleFn<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
  let lastCallTime = 0;

  return (...args: Parameters<T>) => {
    const now = Date.now();

    if (now - lastCallTime >= wait) {
      lastCallTime = now;
      func(...args);
    }
  };
}
